import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { collaboratorSalaryService } from '../../Services/collaboratorSalaryService';
import {
  CollaboratorSalary,
  SalaryConfig,
  hasSalaryPermission,
} from '../../Models/collaboratorSalary.types';
import { SalaryHistoryEntryWithId } from '../../Models/salaryHistory.types';

interface SalaryTimelineEntry extends SalaryHistoryEntryWithId {
  source?: 'history' | 'legacy';
}

interface SalaryViewModel {
  config: SalaryConfig | null;
  currentEntry: SalaryTimelineEntry | null;
  upcomingEntry: SalaryTimelineEntry | null;
}

const computeSalaryView = (
  salary: CollaboratorSalary | null,
  history: SalaryHistoryEntryWithId[]
): SalaryViewModel | null => {
  const config = salary?.config ?? null;
  const normalizedHistory = [...history].sort((a, b) => {
    const aDate = new Date(a.changeDate);
    const bDate = new Date(b.changeDate);
    return aDate.getTime() - bDate.getTime();
  });

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const pastOrPresent = normalizedHistory.filter((entry) => {
    const entryDate = new Date(entry.changeDate);
    entryDate.setHours(0, 0, 0, 0);
    return entryDate.getTime() <= today.getTime();
  });

  const futureEntries = normalizedHistory.filter((entry) => {
    const entryDate = new Date(entry.changeDate);
    entryDate.setHours(0, 0, 0, 0);
    return entryDate.getTime() > today.getTime();
  });

  let currentEntry: SalaryTimelineEntry | null = pastOrPresent.length
    ? { ...pastOrPresent[pastOrPresent.length - 1], source: 'history' }
    : null;

  const upcomingEntry: SalaryTimelineEntry | null = futureEntries.length
    ? { ...futureEntries[0], source: 'history' }
    : null;

  if (!currentEntry && salary?.legacyCurrent) {
    const legacyDate = salary.legacyCurrent.effectiveDate || new Date().toISOString().slice(0, 10);
    currentEntry = {
      id: 'legacy-current',
      amount: salary.legacyCurrent.amount,
      extras: salary.legacyCurrent.extras,
      salaryType: salary.legacyCurrent.salaryType,
      changeDate: legacyDate,
      changeReason: 'Salário vigente (legado)',
      changeObservations: undefined,
      recordedAt: new Date(),
      recordedBy: 'legacy',
      source: 'legacy',
    };
  }

  if (!config && !currentEntry && !upcomingEntry) {
    return null;
  }

  return {
    config,
    currentEntry,
    upcomingEntry,
  };
};

interface UseCollaboratorSalaryOptions {
  includeHistory?: boolean;
  historyLimit?: number;
  enableRealTime?: boolean;
}

interface UseCollaboratorSalaryResult {
  data: SalaryViewModel | null;
  history?: SalaryHistoryEntryWithId[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  isSuccess: boolean;
  refetch: () => void;
}

/**
 * Hook to fetch and manage collaborator salary data
 */
export const useCollaboratorSalary = (
  collaboratorId: string | null,
  options: UseCollaboratorSalaryOptions = {}
): UseCollaboratorSalaryResult => {
  const {
    includeHistory = false,
    historyLimit = 10,
    enableRealTime = false
  } = options;

  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [realTimeSalary, setRealTimeSalary] = useState<CollaboratorSalary | null>(null);
  const [realTimeHistory, setRealTimeHistory] = useState<SalaryHistoryEntryWithId[]>([]);

  const userRoles = user?.roles || [];
  const canView = hasSalaryPermission(userRoles, 'canViewSalary');

  // Main salary query
  const salaryQuery = useQuery({
    queryKey: ['collaboratorSalary', collaboratorId],
    queryFn: async () => {
      if (!collaboratorId || !canView) {
        return null;
      }
      return collaboratorSalaryService.getSalary(collaboratorId);
    },
    enabled: !!collaboratorId && canView,
  });

  // History query (optional)
  const historyQuery = useQuery({
    queryKey: ['collaboratorSalaryHistory', collaboratorId, historyLimit],
    queryFn: async () => {
      if (!collaboratorId || !canView) {
        return [];
      }
      return collaboratorSalaryService.getSalaryHistoryWithIds(collaboratorId, historyLimit);
    },
    enabled: !!collaboratorId && canView && includeHistory,
  });

  // Set up real-time listeners
  useEffect(() => {
    if (!enableRealTime || !collaboratorId || !canView) {
      return;
    }

    // Subscribe to salary changes
    const unsubscribeSalary = collaboratorSalaryService.onSalaryChange(
      collaboratorId,
      (salary) => {
        setRealTimeSalary(salary);
        // Update query cache
        queryClient.setQueryData(
          ['collaboratorSalary', collaboratorId],
          salary
        );
      }
    );

    // Subscribe to history changes if needed
    let unsubscribeHistory: (() => void) | undefined;
    if (includeHistory) {
      unsubscribeHistory = collaboratorSalaryService.onSalaryHistoryChange(
        collaboratorId,
        (history) => {
          setRealTimeHistory(history);
          // Update query cache
          queryClient.setQueryData(
            ['collaboratorSalaryHistory', collaboratorId, historyLimit],
            history
          );
        },
        historyLimit
      );
    }

    // Cleanup
    return () => {
      unsubscribeSalary();
      unsubscribeHistory?.();
    };
  }, [collaboratorId, canView, enableRealTime, includeHistory, historyLimit, queryClient]);

  const baseSalaryData = enableRealTime ? realTimeSalary : (salaryQuery.data ?? null);
  const historyData = includeHistory
    ? (enableRealTime ? realTimeHistory : (historyQuery.data ?? []))
    : [];

  const derivedSalary = computeSalaryView(baseSalaryData, historyData);

  return {
    data: derivedSalary,
    history: includeHistory ? historyData : undefined,
    isLoading: salaryQuery.isLoading || (includeHistory && historyQuery.isLoading),
    isError: salaryQuery.isError || (includeHistory && historyQuery.isError),
    error: salaryQuery.error || historyQuery.error || null,
    isSuccess: salaryQuery.isSuccess,
    refetch: () => {
      salaryQuery.refetch();
      if (includeHistory) {
        historyQuery.refetch();
      }
    },
  };
};

/**
 * Hook for salary mutations (update, delete history)
 */
export const useCollaboratorSalaryMutation = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userRoles = user?.roles || [];

  // Update salary mutation
  const updateSalaryMutation = useMutation({
    mutationFn: async ({
      collaboratorId,
      salary,
      reason,
      observations,
    }: {
      collaboratorId: string;
      salary: {
        amount: number;
        extras: number;
        salaryType: 'CLT' | 'PJ' | 'Estagiário' | 'Sócio';
        effectiveDate: string;
      };
      reason: string;
      observations?: string;
    }) => {
      if (!hasSalaryPermission(userRoles, 'canEditSalary')) {
        throw new Error('Insufficient permissions to edit salary');
      }

      return collaboratorSalaryService.updateSalary(
        collaboratorId,
        salary,
        reason,
        observations,
        user?.uid
      );
    },
    onSuccess: (_, variables) => {
      // Invalidate related queries
      queryClient.invalidateQueries({
        queryKey: ['collaboratorSalary', variables.collaboratorId]
      });
      queryClient.invalidateQueries({
        queryKey: ['collaboratorSalaryHistory', variables.collaboratorId]
      });
    },
  });

  // Delete history mutation
  const deleteHistoryMutation = useMutation({
    mutationFn: async ({
      collaboratorId,
      historyId,
    }: {
      collaboratorId: string;
      historyId: string;
    }) => {
      return collaboratorSalaryService.deleteSalaryHistory(
        collaboratorId,
        historyId,
        userRoles
      );
    },
    onSuccess: (_, variables) => {
      // Invalidate history query
      queryClient.invalidateQueries({
        queryKey: ['collaboratorSalaryHistory', variables.collaboratorId]
      });
    },
  });

  // Update config mutation
  const updateConfigMutation = useMutation({
    mutationFn: async ({
      collaboratorId,
      config,
    }: {
      collaboratorId: string;
      config: {
        allowRemove?: boolean;
        showHistory?: boolean;
      };
    }) => {
      if (!hasSalaryPermission(userRoles, 'canEditSalary')) {
        throw new Error('Insufficient permissions to edit salary configuration');
      }

      return collaboratorSalaryService.updateSalaryConfig(
        collaboratorId,
        config,
        user?.uid
      );
    },
    onSuccess: (_, variables) => {
      // Invalidate salary query
      queryClient.invalidateQueries({
        queryKey: ['collaboratorSalary', variables.collaboratorId]
      });
    },
  });

  return {
    updateSalary: updateSalaryMutation,
    deleteHistory: deleteHistoryMutation,
    updateConfig: updateConfigMutation,
  };
};
